/* eslint-disable custom-eslint-rules/force-logicalOperandsMustBeBoolean */
import { useCallback, useMemo } from "react";
import { SelectV2Option, SelectV2Value } from "../SelectV2.types.js";

type UseSelectValueProps<Value extends SelectV2Value> = {
    value: Value | Value[] | undefined;
    options: SelectV2Option<Value>[];
    onChange: (value: Value | Value[] | undefined) => void;
    multiple: boolean;
    closeMenu: () => void;
};

export const useSelectValue = <Value extends SelectV2Value>({ value, options, onChange, multiple, closeMenu }: UseSelectValueProps<Value>) => {
    // Get labels for selected values (used for displaying in the input)
    const valueLabels = useMemo(() => {
        if (multiple) {
            return (
                (value as Value[])
                    ?.map((v) => options.find((o) => o.value === v)?.label)
                    .filter(Boolean)
                    .join(", ") || ""
            );
        }
        return options.find((o) => o.value === value)?.label || "";
    }, [multiple, value, options]);

    // Handle value change
    const handleChangeValue = useCallback(
        (newValue: Value | Value[] | undefined) => {
            onChange(newValue);
        },
        [onChange],
    );

    // Handle option click
    const handleOptionClick = useCallback(
        (optionValue: Value) => {
            if (multiple) {
                const values = (value as Value[]) || [];
                const newValue = values.includes(optionValue) ? values.filter((v) => v !== optionValue) : [...values, optionValue];
                handleChangeValue(newValue);
            } else {
                handleChangeValue(optionValue);
                closeMenu();
            }
        },
        [multiple, value, handleChangeValue, closeMenu],
    );

    // Clear selection
    const clear = useCallback(() => {
        if (multiple) {
            handleChangeValue([] as Value[]);
        } else {
            handleChangeValue(undefined);
        }
    }, [multiple, handleChangeValue]);

    return {
        valueLabels,
        handleChangeValue,
        handleOptionClick,
        clear,
    };
};
